import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import store from '../redux/store';
import { 
  selectComponents, 
  selectProjectName, 
  selectLayoutInfo,
  selectMenus
} from '../redux/editorSlice';
import { layoutOptions } from '../layouts';

// 프로젝트 소스 파일 생성 및 다운로드 (npm start로 실행 가능한 버전)
const buildAndDownloadBuildFolder = async () => {
  try {
    // Redux 스토어에서 데이터 가져오기
    const state = store.getState();
    const components = selectComponents(state);
    const projectName = selectProjectName(state);
    const layoutInfo = selectLayoutInfo(state);
    const menus = selectMenus(state);
    
    if (!components || components.length === 0) {
      return { success: false, error: '내보낼 컴포넌트가 없습니다. 먼저 컴포넌트를 추가해주세요.' };
    }
    
    // ZIP 파일 생성 (프로젝트 구조 모방)
    const zip = new JSZip();
    
    // README.md 파일 추가 - 사용 안내
    const readmeContent = `# ${projectName}

## 프로젝트 실행 방법
1. node.js와 npm이 설치되어 있어야 합니다.
2. 아래 명령어로 의존성 패키지를 설치합니다:
   \`\`\`
   npm install
   \`\`\`
3. 개발 서버를 시작합니다:
   \`\`\`
   npm start
   \`\`\`
4. 브라우저에서 http://localhost:3000 으로 접속하여 웹사이트를 확인합니다.

## 프로젝트 구조
- \`src/\`: 소스 코드 폴더
  - \`components/\`: 컴포넌트 폴더
  - \`layouts/\`: 레이아웃 관련 파일
  - \`redux/\`: 상태 관리 관련 파일
  - \`pages/\`: 페이지 컴포넌트
  - \`App.js\`: 메인 앱 컴포넌트
  - \`index.js\`: 진입점
- \`public/\`: 정적 파일 폴더
- \`package.json\`: 프로젝트 설정 및 의존성 정보

## Deploy 및 배포
프로덕션 Deploy를 생성하려면 다음 명령어를 실행합니다:
\`\`\`
npm run build
\`\`\`

Deploy된 파일은 \`build/\` 폴더에 생성됩니다.
`;
    zip.file('README.md', readmeContent);
    
    // package.json 파일 추가
    const packageJson = {
      "name": projectName.toLowerCase().replace(/\s+/g, '-'),
      "version": "0.1.0",
      "private": true,
      "dependencies": {
        "@reduxjs/toolkit": "^1.9.7",
        "@testing-library/jest-dom": "^5.17.0",
        "@testing-library/react": "^13.4.0",
        "@testing-library/user-event": "^13.5.0",
        "bootstrap": "^5.3.2",
        "file-saver": "^2.0.5",
        "jszip": "^3.10.1",
        "react": "^18.2.0",
        "react-dom": "^18.2.0",
        "react-redux": "^8.1.3",
        "react-router-dom": "^6.20.0",
        "react-scripts": "5.0.1",
        "web-vitals": "^2.1.4"
      },
      "scripts": {
        "start": "react-scripts start",
        "build": "react-scripts build",
        "test": "react-scripts test",
        "eject": "react-scripts eject"
      },
      "eslintConfig": {
        "extends": [
          "react-app",
          "react-app/jest"
        ]
      },
      "browserslist": {
        "production": [
          ">0.2%",
          "not dead",
          "not op_mini all"
        ],
        "development": [
          "last 1 chrome version",
          "last 1 firefox version",
          "last 1 safari version"
        ]
      }
    };
    
    zip.file('package.json', JSON.stringify(packageJson, null, 2));
    
    // src 폴더 생성
    const srcFolder = zip.folder('src');
    
    // public 폴더 생성
    const publicFolder = zip.folder('public');
    
    // public 파일 추가
    publicFolder.file('index.html', `<!DOCTYPE html>
<html lang="ko">
  <head>
    <meta charset="utf-8" />
    <link rel="icon" href="%PUBLIC_URL%/favicon.ico" />
    <meta name="viewport" content="width=device-width, initial-scale=1" />
    <meta name="theme-color" content="#000000" />
    <meta name="description" content="${projectName} - 웹 빌더로 생성된 웹사이트" />
    <link rel="apple-touch-icon" href="%PUBLIC_URL%/logo192.png" />
    <link rel="manifest" href="%PUBLIC_URL%/manifest.json" />
    <link href="https://fonts.googleapis.com/css2?family=Noto+Sans+KR:wght@300;400;500;700&display=swap" rel="stylesheet">
    <link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/bootstrap@4.6.2/dist/css/bootstrap.min.css" integrity="sha384-xOolHFLEh07PJGoPkLv1IbcEPTNtaed2xpHsD9ESMhqIYd0nLMwNLD69Npy4HI+N" crossorigin="anonymous">
    <title>${projectName}</title>
  </head>
  <body>
    <noscript>이 앱을 실행하려면 JavaScript를 활성화해야 합니다.</noscript>
    <div id="root"></div>
  </body>
</html>`);
    
    publicFolder.file('manifest.json', JSON.stringify({
      "short_name": projectName,
      "name": projectName,
      "icons": [
        {
          "src": "favicon.ico",
          "sizes": "64x64 32x32 24x24 16x16",
          "type": "image/x-icon"
        },
        {
          "src": "logo192.png",
          "type": "image/png",
          "sizes": "192x192"
        },
        {
          "src": "logo512.png",
          "type": "image/png",
          "sizes": "512x512"
        }
      ],
      "start_url": ".",
      "display": "standalone",
      "theme_color": "#000000",
      "background_color": "#ffffff"
    }, null, 2));
    
    // favicon.ico 및 기본 로고 파일 추가
    publicFolder.file('favicon.ico', '');

    // Redux 상태에서 withcookieData 가져오기
    try {
      const withcookieData = localStorage.getItem('withcookieFullData');
      if (withcookieData) {
        const parsedData = JSON.parse(withcookieData);
        
        // favicon_ico_url이 있는 경우 
        if (parsedData?.skin?.theme?.favicon_ico_url) {
          // favicon URL을 가져와서 다운로드
          try {
            const faviconResponse = await fetch(parsedData.skin.theme.favicon_ico_url);
            if (faviconResponse.ok) {
              const faviconBlob = await faviconResponse.blob();
              // 다운로드한 favicon.ico 파일 업데이트
              publicFolder.file('favicon.ico', faviconBlob);
              
              // public/index.html에 favicon 설정 추가
              const indexHtmlContent = publicFolder.file('index.html').asText();
              const updatedHtmlContent = indexHtmlContent.replace(
                '<link rel="icon" href="%PUBLIC_URL%/favicon.ico" />',
                `<link rel="icon" href="%PUBLIC_URL%/favicon.ico" />`
              );
              publicFolder.file('index.html', updatedHtmlContent);
            }
          } catch (faviconError) {
            console.error('Favicon 다운로드 중 오류:', faviconError);
          }
        }
        
        // favicon16x_url이 있는 경우
        if (parsedData?.skin?.theme?.favicon16x_url) {
          try {
            const favicon16Response = await fetch(parsedData.skin.theme.favicon16x_url);
            if (favicon16Response.ok) {
              const favicon16Blob = await favicon16Response.blob();
              publicFolder.file('favicon-16x16.png', favicon16Blob);
              
              // public/index.html에 favicon16x16 설정 추가
              const indexHtmlContent = publicFolder.file('index.html').asText();
              if (!indexHtmlContent.includes('favicon-16x16.png')) {
                const updatedHtmlContent = indexHtmlContent.replace(
                  '</head>',
                  `  <link rel="icon" type="image/png" sizes="16x16" href="%PUBLIC_URL%/favicon-16x16.png" />
</head>`
                );
                publicFolder.file('index.html', updatedHtmlContent);
              }
            }
          } catch (favicon16Error) {
            console.error('Favicon 16x16 다운로드 중 오류:', favicon16Error);
          }
        }
        
        // favicon32x_url이 있는 경우
        if (parsedData?.skin?.theme?.favicon32x_url) {
          try {
            const favicon32Response = await fetch(parsedData.skin.theme.favicon32x_url);
            if (favicon32Response.ok) {
              const favicon32Blob = await favicon32Response.blob();
              publicFolder.file('favicon-32x32.png', favicon32Blob);
              
              // public/index.html에 favicon32x32 설정 추가
              const indexHtmlContent = publicFolder.file('index.html').asText();
              if (!indexHtmlContent.includes('favicon-32x32.png')) {
                const updatedHtmlContent = indexHtmlContent.replace(
                  '</head>',
                  `  <link rel="icon" type="image/png" sizes="32x32" href="%PUBLIC_URL%/favicon-32x32.png" />
</head>`
                );
                publicFolder.file('index.html', updatedHtmlContent);
              }
            }
          } catch (favicon32Error) {
            console.error('Favicon 32x32 다운로드 중 오류:', favicon32Error);
          }
        }
      }
    } catch (reduxError) {
      console.error('Redux 상태에서 favicon URL 가져오기 오류:', reduxError);
    }

    publicFolder.file('logo192.png', '');
    publicFolder.file('logo512.png', '');
    publicFolder.file('robots.txt', 'User-agent: *\nDisallow:');
    
    // 이미지 파일이 있는 경우 추가
    const imagesFolder = srcFolder.folder('images');
    
    // 컴포넌트에서 이미지 URL 추출하여 추가
    const imageUrls = extractImageUrls(components);
    
    // 이미지 복사 처리를 위한 프라미스 배열
    const imagePromises = [];
    
    for (let i = 0; i < imageUrls.length; i++) {
      try {
        const imageUrl = imageUrls[i];
        
        if (!imageUrl) continue;
        
        if (imageUrl.startsWith('data:')) {
          // 데이터 URL인 경우 직접 추가
          const base64Data = imageUrl.split(',')[1];
          if (base64Data) {
            imagesFolder.file(`image${i}.png`, base64Data, { base64: true });
          }
        } else if (!imageUrl.startsWith('http')) {
          // 로컬 이미지인 경우 처리
          const absoluteImageUrl = imageUrl.startsWith('/') 
            ? imageUrl 
            : `/${imageUrl}`;
          
          // 이미지를 가져와서 추가하는 프라미스 생성
          const imagePromise = fetch(absoluteImageUrl)
            .then(response => {
              if (!response.ok) throw new Error(`이미지 로드 실패: ${absoluteImageUrl}`);
              return response.blob();
            })
            .then(blob => {
              const fileName = imageUrl.split('/').pop() || `image${i}.png`;
              imagesFolder.file(fileName, blob);
              return { success: true, url: imageUrl, fileName };
            })
            .catch(error => {
              console.warn(`이미지 처리 실패 (${imageUrl}):`, error.message);
              return { success: false, url: imageUrl, error: error.message };
            });
          
          imagePromises.push(imagePromise);
        }
      } catch (error) {
        console.error('이미지 처리 중 오류:', error);
      }
    }
    
    // 모든 이미지 처리 완료 대기
    if (imagePromises.length > 0) {
      const imageResults = await Promise.allSettled(imagePromises);
    }
    
    // index.js 파일 추가
    srcFolder.file('index.js', `import React from 'react';
import ReactDOM from 'react-dom/client';
import { Provider } from 'react-redux';
import { store } from './redux/store';
import './index.css';
import App from './App';

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <Provider store={store}>
      <App />
    </Provider>
  </React.StrictMode>
);
`);
    
    // index.css 파일 추가
    srcFolder.file('index.css', `body {
  margin: 0;
  font-family: 'Noto Sans KR', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen',
    'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue',
    sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

code {
  font-family: source-code-pro, Menlo, Monaco, Consolas, 'Courier New',
    monospace;
}
`);
    
    // App.js 파일 추가
    srcFolder.file('App.js', `import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { selectComponents, selectLayoutInfo, selectMenus } from './redux/editorSlice';
import { fetchWithcookieData } from './redux/WithcookieSlice';
import { getLayoutComponentById } from './layouts';
import './App.css';
import MainContent from './components/MainContent';

function App() {
  const dispatch = useDispatch();
  // 상태에서 필요한 정보 가져오기
  const components = useSelector(selectComponents);
  const layoutInfo = useSelector(selectLayoutInfo);
  const menus = useSelector(selectMenus);
  
  // 에디터에서 선택한 레이아웃 정보 확인 및 사용
  // 이전에 하드코딩된 레이아웃 목록 대신 동적으로 결정
  const selectedLayoutInfo = layoutOptions.find(l => l.id === layoutInfo.selectedLayout) || layoutOptions[0];
  
  // Withcookie 데이터 로드 및 favicon 설정
  useEffect(() => {
    const loadWithcookieData = async () => {
      try {
        // Redux 액션을 통해 데이터 가져오기
        const data = await dispatch(fetchWithcookieData());
        
        // favicon_ico_url이 있으면 파비콘 업데이트
        if (data?.skin?.theme?.favicon_ico_url) {
          // 기존 favicon 링크 요소 찾기
          const existingFavicon = document.querySelector("link[rel='icon']");
          
          if (existingFavicon) {
            // 기존 favicon이 있으면 href 속성만 변경
            existingFavicon.href = data.skin.theme.favicon_ico_url;
          } else {
            // 기존 favicon이 없으면 새로 생성하여 추가
            const newFavicon = document.createElement("link");
            newFavicon.rel = "icon";
            newFavicon.href = data.skin.theme.favicon_ico_url;
            document.head.appendChild(newFavicon);
          }
        }
        
        // favicon16x_url이 있으면 16x16 파비콘 업데이트
        if (data?.skin?.theme?.favicon16x_url) {
          // 기존 16x16 favicon 링크 요소 찾기
          const existingFavicon16 = document.querySelector("link[rel='icon'][sizes='16x16']");
          
          if (existingFavicon16) {
            existingFavicon16.href = data.skin.theme.favicon16x_url;
          } else {
            // 새로운 파비콘 요소를 생성하여 속성을 설정합니다
            const newFavicon16 = document.createElement("link");
            newFavicon16.rel = "icon";
            newFavicon16.href = data.skin.theme.favicon16x_url;
            newFavicon16.setAttribute('sizes', '16x16');
            newFavicon16.type = "image/png";
            document.head.appendChild(newFavicon16);
          }
        }
        
        // favicon32x_url이 있으면 32x32 파비콘 업데이트
        if (data?.skin?.theme?.favicon32x_url) {
          // 기존 32x32 favicon 링크 요소 찾기
          const existingFavicon32 = document.querySelector("link[rel='icon'][sizes='32x32']");
          
          if (existingFavicon32) {
            existingFavicon32.href = data.skin.theme.favicon32x_url;
          } else {
            // 새로운 파비콘 요소를 생성하여 속성을 설정합니다
            const newFavicon32 = document.createElement("link");
            newFavicon32.rel = "icon";
            newFavicon32.href = data.skin.theme.favicon32x_url;
            newFavicon32.setAttribute('sizes', '32x32');
            newFavicon32.type = "image/png";
            document.head.appendChild(newFavicon32);
          }
        }
      } catch (error) {
        console.error('Withcookie 데이터 로드 실패:', error);
      }
    };
    
    loadWithcookieData();
  }, [dispatch]);

  // 동적으로 결정된 레이아웃 정보를 사용하여 layoutOptions 배열 생성
  const dynamicLayoutOptions = [
    {
      id: selectedLayoutInfo.id,
      name: selectedLayoutInfo.name,
      component: selectedLayoutInfo.component,
      thumbnail: selectedLayoutInfo.thumbnail,
      category: selectedLayoutInfo.category,
      options: selectedLayoutInfo.options
    }
  ];
  
  // 레이아웃 컴포넌트 가져오기
  const selectedLayoutId = layoutInfo?.selectedLayout || 'layout01';
  const LayoutComponent = getLayoutComponentById(selectedLayoutId);
  
  return (
    <div className="App">
      <LayoutComponent>
        <MainContent components={components} menus={menus} />
      </LayoutComponent>
    </div>
  );
}

export default App;
`);
    
    // App.css 파일 추가
    srcFolder.file('App.css', `.App {
  width: 100%;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

.component {
  margin-bottom: 16px;
}

.text-component {
  line-height: 1.5;
}

.image-component img {
  max-width: 100%;
  height: auto;
}

.button-component {
  display: inline-block;
  padding: 8px 16px;
  background-color: #4361ee;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.9rem;
  text-decoration: none;
  transition: background-color 0.2s;
}

.button-component:hover {
  background-color: #3a56d4;
}

.container-component {
  padding: 15px;
  border: 1px solid #eee;
  border-radius: 4px;
}

/* 레이아웃 스타일 */
.container {
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 15px;
}

/* 기본 레이아웃 스타일 */
.layout-header {
  background-color: #fff;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  padding: 15px 0;
}

.layout-header .main-nav {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.layout-header .logo {
  font-size: 24px;
  font-weight: bold;
  text-decoration: none;
  color: #333;
}

.layout-header .main-menu {
  display: flex;
  list-style: none;
  margin: 0;
  padding: 0;
}

.layout-header .main-menu li {
  margin-left: 20px;
}

.layout-header .main-menu a {
  text-decoration: none;
  color: #333;
  font-weight: 500;
  transition: color 0.2s;
}

.layout-header .main-menu a:hover {
  color: #4361ee;
}

.layout-main-content {
  flex: 1;
  padding: 30px 0;
}

.layout-footer {
  background-color: #f8f9fa;
  padding: 20px 0;
  margin-top: auto;
}

/* 레이아웃 2 스타일 - 중앙 정렬 헤더 */
.layout-header-centered .header-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.layout-header-centered .logo {
  margin-bottom: 15px;
}

.layout-header-centered .main-menu {
  justify-content: center;
}

/* 레이아웃 3 스타일 - 투명 헤더 */
.layout-header-transparent {
  background-color: transparent;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  z-index: 10;
  box-shadow: none;
}

.layout-header-transparent .main-menu a {
  color: #fff;
}

.layout-header-transparent .logo {
  color: #fff;
}

.hero-section {
  background-color: #4361ee;
  color: #fff;
  padding: 100px 0 50px;
  text-align: center;
  margin-bottom: 30px;
}

.hero-section h1 {
  font-size: 32px;
  margin-bottom: 10px;
}

/* 반응형 스타일 */
@media (max-width: 768px) {
  .layout-header .main-nav {
    flex-direction: column;
  }
  
  .layout-header .logo {
    margin-bottom: 15px;
  }
  
  .layout-header .main-menu {
    justify-content: center;
  }
  
  .layout-header .main-menu li {
    margin: 0 10px;
  }
}
`);
    
    // components 폴더 생성
    const componentsFolder = srcFolder.folder('components');
    
    // MainContent 컴포넌트 추가
    componentsFolder.file('MainContent.js', `import React, { useState } from 'react';
import ComponentRenderer from './ComponentRenderer';

const MainContent = ({ components, menus }) => {
  const [path, setPath] = useState(window.location.pathname);
  
  // 라우팅 처리
  React.useEffect(() => {
    // 내비게이션 이벤트 처리
    const handleNavigation = (e) => {
      if (e.target.tagName === 'A' && e.target.getAttribute('href') && e.target.getAttribute('href').startsWith('/')) {
        e.preventDefault();
        const newPath = e.target.getAttribute('href');
        window.history.pushState(null, '', newPath);
        setPath(newPath);
      }
    };
    
    // 뒤로가기/앞으로가기 처리
    const handlePopState = () => {
      setPath(window.location.pathname);
    };
    
    document.addEventListener('click', handleNavigation);
    window.addEventListener('popstate', handlePopState);
    
    return () => {
      document.removeEventListener('click', handleNavigation);
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);
  
  // 현재 경로에 따른 컴포넌트 목록 결정
  const getPageComponents = () => {
    // 메뉴에 할당된 컴포넌트 검색
    const menuComponents = {};
    menus.forEach(menu => {
      if (menu.path && menu.components) {
        menuComponents[menu.path] = menu.components;
      }
    });
    
    // 현재 경로에 맞는 컴포넌트 목록
    if (path && menuComponents[path]) {
      return menuComponents[path].map(id => 
        components.find(comp => comp.id === id)
      ).filter(comp => comp);
    } else if (path === '/' || !path) {
      // 홈 페이지 - 메뉴에 할당되지 않은 컴포넌트 표시
      return components.filter(comp => {
        // 부모가 없는 컴포넌트만 (최상위 컴포넌트)
        if (comp.parentId) return false;
        
        // 메뉴에 할당되지 않은 컴포넌트인지 확인
        let isAssignedToMenu = false;
        Object.values(menuComponents).forEach(ids => {
          if (ids.includes(comp.id)) {
            isAssignedToMenu = true;
          }
        });
        
        return !isAssignedToMenu;
      });
    }
    
    // 경로가 일치하지 않으면 빈 배열 반환 (404 페이지)
    return [];
  };
  
  // 현재 페이지 컴포넌트
  const pageComponents = getPageComponents();
  
  return (
    <div className="main-content">
      {pageComponents.length > 0 ? (
        pageComponents.map(component => (
          <ComponentRenderer key={component.id} component={component} components={components} />
        ))
      ) : (
        <div className="error-page">
          <h1>404</h1>
          <p>페이지를 찾을 수 없습니다.</p>
        </div>
      )}
    </div>
  );
};

export default MainContent;
`);
    
    // ComponentRenderer 컴포넌트 추가
    componentsFolder.file('ComponentRenderer.js', `import React from 'react';

const ComponentRenderer = ({ component, components }) => {
  // 컴포넌트가 없으면 표시하지 않음
  if (!component) return null;
  
  // 스타일 객체 변환
  const styles = component.style || {};
  
  // 컴포넌트 타입에 따라 다른 렌더링
  switch (component.type) {
    case 'TEXT':
      return (
        <div 
          id={\`component-\${component.id}\`}
          className="component text-component"
          style={styles}
          dangerouslySetInnerHTML={{ __html: component.content || '' }}
        />
      );
      
    case 'IMAGE':
      // 이미지 URL 처리 - 상대 경로 변환
      let imageSrc = component.content?.src || '';
      if (imageSrc && !imageSrc.startsWith('http') && !imageSrc.startsWith('data:')) {
        // 로컬 이미지 경로를 static/media 폴더 참조로 변경
        const filename = imageSrc.split('/').pop();
        imageSrc = \`/images/\${filename}\`;
      }
      
      return (
        <div 
          id={\`component-\${component.id}\`}
          className="component image-component"
          style={styles}
        >
          <img 
            src={imageSrc} 
            alt={component.content?.alt || ''} 
            style={{ maxWidth: '100%' }}
          />
        </div>
      );
      
    case 'BUTTON':
      return (
        <div 
          id={\`component-\${component.id}\`}
          className="component button-component-wrapper"
          style={styles}
        >
          <button className="button-component">
            {component.content || 'Button'}
          </button>
        </div>
      );
      
    case 'CONTAINER':
      // 컨테이너에 포함된 자식 컴포넌트 찾기
      const childComponents = components
        .filter(c => c.parentId === component.id)
        .sort((a, b) => (a.order || 0) - (b.order || 0));
      
      return (
        <div 
          id={\`component-\${component.id}\`}
          className="component container-component"
          style={styles}
        >
          {childComponents.map(childComponent => (
            <ComponentRenderer key={childComponent.id} component={childComponent} components={components} />
          ))}
        </div>
      );
    
    case 'BOARD':
      return (
        <div 
          id={\`component-\${component.id}\`}
          className="component board-component"
          style={styles}
        >
          <h3>게시판 컴포넌트</h3>
          <div className="board-container">
            <div className="board-header">
              <div className="row">
                <div className="col-1">번호</div>
                <div className="col-6">제목</div>
                <div className="col-2">작성자</div>
                <div className="col-3">작성일</div>
              </div>
            </div>
            <div className="board-body">
              <div className="row">
                <div className="col-1">1</div>
                <div className="col-6">샘플 게시글 제목입니다.</div>
                <div className="col-2">관리자</div>
                <div className="col-3">{new Date().toLocaleDateString()}</div>
              </div>
            </div>
          </div>
        </div>
      );
      
    case 'LOGIN':
      return (
        <div 
          id={\`component-\${component.id}\`}
          className="component login-component"
          style={styles}
        >
          <h3>로그인</h3>
          <form className="login-form">
            <div className="form-group">
              <label htmlFor="username">아이디</label>
              <input type="text" className="form-control" id="username" placeholder="아이디를 입력하세요" />
            </div>
            <div className="form-group">
              <label htmlFor="password">비밀번호</label>
              <input type="password" className="form-control" id="password" placeholder="비밀번호를 입력하세요" />
            </div>
            <button type="submit" className="btn btn-primary">로그인</button>
          </form>
        </div>
      );
    
    default:
      return (
        <div 
          id={\`component-\${component.id}\`}
          className={\`component \${component.type.toLowerCase()}-component\`}
          style={styles}
        >
          지원되지 않는 컴포넌트 타입: {component.type}
        </div>
      );
  }
};

export default ComponentRenderer;
`);
    
    // src/layouts 폴더 생성 및 기본 파일 추가
    const layoutsFolder = srcFolder.folder('layouts');
    
    // 원본 레이아웃 index.js 파일 추가 - 이 부분을 프로젝트 내 실제 파일로 대체
    layoutsFolder.file('index.js', `import LayoutOne from './layout01/layouts/LayoutOne';
import LayoutTwo from './layout01/layouts/LayoutTwo';
import LayoutThree from './layout01/layouts/LayoutThree';
import LayoutFour from './layout01/layouts/LayoutFour';
import LayoutFive from './layout01/layouts/LayoutFive';
import LayoutSix from './layout01/layouts/LayoutSix';
import LayoutSeven from './layout01/layouts/LayoutSeven';
import LayoutEight from './layout01/layouts/LayoutEight';
import LayoutNine from './layout01/layouts/LayoutNine';
import LayoutTen from './layout01/layouts/LayoutTen';
import LayoutBoard from './layout01/layouts/LayoutBoard';
import LayoutGallery from './layout01/layouts/LayoutGallery';
import LayoutBlog from './layout01/layouts/LayoutBlog';

// 레이아웃 썸네일 기본 이미지
import layoutThumb1 from './layout01/assets/img/logo/logo.png';

// 레이아웃별 로고 정보
export const layoutLogoMapping = {
  'layout01': {
    logoPath: './layout01/assets/img/logo/logo.png',
    logo2Path: './layout01/assets/img/logo/logo-2.png'
  }
};

// 레이아웃 ID로 로고 이미지 경로 가져오기 함수
export const getLogoPathByLayoutId = (layoutId) => {
  const mapping = layoutLogoMapping[layoutId];
  return mapping ? mapping.logoPath : layoutLogoMapping['layout01'].logoPath; // 기본값으로 layout01 로고 사용
};

export const getLogo2PathByLayoutId = (layoutId) => {
  const mapping = layoutLogoMapping[layoutId];
  return mapping ? mapping.logo2Path : layoutLogoMapping['layout01'].logo2Path; // 기본값으로 layout01 로고 사용
};

// 기본 레이아웃 옵션
const defaultLayoutOptions = [
  {
    name: 'headerPosition',
    label: '헤더 위치',
    type: 'select',
    options: ['top', 'fixed'],
    default: 'top'
  },
  {
    name: 'showSidebar',
    label: '사이드바 표시',
    type: 'boolean',
    default: false
  },
  {
    name: 'sidebarPosition',
    label: '사이드바 위치',
    type: 'select',
    options: ['left', 'right'],
    default: 'right'
  }
];

// 게시판 레이아웃 옵션
const boardLayoutOptions = [
  ...defaultLayoutOptions,
  {
    name: 'boardType',
    label: '게시판 타입',
    type: 'select',
    options: ['default', 'gallery', 'list'],
    default: 'default'
  }
];

// 갤러리 레이아웃 옵션
const galleryLayoutOptions = [
  ...defaultLayoutOptions,
  {
    name: 'galleryType',
    label: '갤러리 타입',
    type: 'select',
    options: ['grid', 'masonry', 'carousel'],
    default: 'grid'
  },
  {
    name: 'columns',
    label: '열 개수',
    type: 'number',
    min: 1,
    max: 6,
    default: 3
  },
  {
    name: 'showFilter',
    label: '필터 표시',
    type: 'boolean',
    default: true
  }
];

// 블로그 레이아웃 옵션
const blogLayoutOptions = [
  ...defaultLayoutOptions,
  {
    name: 'blogType',
    label: '블로그 타입',
    type: 'select',
    options: ['default', 'masonry', 'grid'],
    default: 'default'
  },
  {
    name: 'showFeatured',
    label: '추천 게시물 표시',
    type: 'boolean',
    default: true
  }
];

export const layoutOptions = [
  {
    id: 'layout01',
    name: '레이아웃 1',
    component: LayoutOne,
    thumbnail: layoutThumb1,
    category: '기본',
    options: defaultLayoutOptions
  },
  {
    id: 'layout02',
    name: '레이아웃 2',
    component: LayoutTwo,
    thumbnail: layoutThumb1,
    category: '기본',
    options: defaultLayoutOptions
  },
  {
    id: 'layout03',
    name: '레이아웃 3',
    component: LayoutThree,
    thumbnail: layoutThumb1,
    category: '기본',
    options: defaultLayoutOptions
  },
  {
    id: 'layout04',
    name: '레이아웃 4',
    component: LayoutFour,
    thumbnail: layoutThumb1,
    category: '기본',
    options: defaultLayoutOptions
  },
  {
    id: 'layout05',
    name: '레이아웃 5',
    component: LayoutFive,
    thumbnail: layoutThumb1,
    category: '기본',
    options: defaultLayoutOptions
  },
  {
    id: 'layout06',
    name: '레이아웃 6',
    component: LayoutSix,
    thumbnail: layoutThumb1,
    category: '기본',
    options: defaultLayoutOptions
  },
  {
    id: 'layout07',
    name: '레이아웃 7',
    component: LayoutSeven,
    thumbnail: layoutThumb1,
    category: '기본',
    options: defaultLayoutOptions
  },
  {
    id: 'layout08',
    name: '레이아웃 8',
    component: LayoutEight,
    thumbnail: layoutThumb1,
    category: '기본',
    options: defaultLayoutOptions
  },
  {
    id: 'layout09',
    name: '레이아웃 9',
    component: LayoutNine,
    thumbnail: layoutThumb1,
    category: '기본',
    options: defaultLayoutOptions
  },
  {
    id: 'layout10',
    name: '레이아웃 10',
    component: LayoutTen,
    thumbnail: layoutThumb1,
    category: '기본',
    options: defaultLayoutOptions
  },
  {
    id: 'layout-board',
    name: '게시판 레이아웃',
    component: LayoutBoard,
    thumbnail: layoutThumb1,
    category: '특수',
    options: boardLayoutOptions
  },
  {
    id: 'layout-gallery',
    name: '갤러리 레이아웃',
    component: LayoutGallery,
    thumbnail: layoutThumb1,
    category: '특수',
    options: galleryLayoutOptions
  },
  {
    id: 'layout-blog',
    name: '블로그 레이아웃',
    component: LayoutBlog,
    thumbnail: layoutThumb1,
    category: '특수',
    options: blogLayoutOptions
  }
];

// 레이아웃 ID로 컴포넌트를 찾는 함수
export const getLayoutComponentById = (id) => {
  const layout = layoutOptions.find(l => l.id === id);
  return layout ? layout.component : null;
};

// 레이아웃 ID로 기본 속성을 찾는 함수
export const getLayoutDefaultPropsById = (id) => {
  const layout = layoutOptions.find(l => l.id === id);
  if (!layout) return {};
  
  // 기본 속성 객체 초기화
  const defaultProps = {};
  
  // 각 옵션에서 기본값 설정
  if (layout.options && Array.isArray(layout.options)) {
    layout.options.forEach(option => {
      if ('default' in option) {
        defaultProps[option.name] = option.default;
      }
    });
  }
  
  return defaultProps;
};
`);

    // layout01 폴더 및 하위 구조 생성
    const layout01Folder = layoutsFolder.folder('layout01');
    
    // layouts 폴더 추가
    const layout01LayoutsFolder = layout01Folder.folder('layouts');
    
    // 기본 레이아웃 컴포넌트들 추가
    layout01LayoutsFolder.file('LayoutOne.js', `import React from 'react';

const LayoutOne = ({ children }) => {
  return (
    <div className="layout-one">
      <header className="layout-header">
        <div className="container">
          <nav className="main-nav">
            <a href="/" className="logo">
              WebBuilder
            </a>
            <ul className="main-menu">
              <li><a href="/">홈</a></li>
              <li><a href="/about">소개</a></li>
              <li><a href="/service">서비스</a></li>
              <li><a href="/contact">문의</a></li>
            </ul>
          </nav>
        </div>
      </header>
      <main className="main-content">
        <div className="container">
          {children}
        </div>
      </main>
      <footer className="layout-footer">
        <div className="container">
          <p>&copy; ${new Date().getFullYear()} ${projectName}. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default LayoutOne;
`);

    layout01LayoutsFolder.file('LayoutTwo.js', `import React from 'react';

const LayoutTwo = ({ children }) => {
  return (
    <div className="layout-two">
      <header className="layout-header layout-header-centered">
        <div className="container">
          <div className="header-content">
            <a href="/" className="logo">
              WebBuilder
            </a>
            <nav className="main-nav">
              <ul className="main-menu">
                <li><a href="/">홈</a></li>
                <li><a href="/about">소개</a></li>
                <li><a href="/service">서비스</a></li>
                <li><a href="/contact">문의</a></li>
              </ul>
            </nav>
          </div>
        </div>
      </header>
      <main className="main-content">
        <div className="container">
          {children}
        </div>
      </main>
      <footer className="layout-footer">
        <div className="container">
          <p>&copy; ${new Date().getFullYear()} ${projectName}. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default LayoutTwo;
`);

    // 나머지 레이아웃 컴포넌트들도 유사하게 추가
    layout01LayoutsFolder.file('LayoutThree.js', `import React from 'react';

const LayoutThree = ({ children }) => {
  return (
    <div className="layout-three">
      <header className="layout-header layout-header-transparent">
        <div className="container">
          <nav className="main-nav">
            <a href="/" className="logo">
              WebBuilder
            </a>
            <ul className="main-menu">
              <li><a href="/">홈</a></li>
              <li><a href="/about">소개</a></li>
              <li><a href="/service">서비스</a></li>
              <li><a href="/contact">문의</a></li>
            </ul>
          </nav>
        </div>
      </header>
      <div className="hero-section">
        <div className="container">
          <h1>WebBuilder로 만드는 멋진 웹사이트</h1>
          <p>간편하게 만들고 배포하세요</p>
        </div>
      </div>
      <main className="main-content">
        <div className="container">
          {children}
        </div>
      </main>
      <footer className="layout-footer">
        <div className="container">
          <p>&copy; ${new Date().getFullYear()} ${projectName}. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default LayoutThree;
`);

    // 기본적인 LayoutFour ~ LayoutTen, LayoutBoard, LayoutGallery, LayoutBlog 컴포넌트 생성
    // (간단한 예시로만 생성하고, 실제 구현은 필요시 추가)
    ['LayoutFour', 'LayoutFive', 'LayoutSix', 'LayoutSeven', 'LayoutEight', 'LayoutNine', 'LayoutTen', 'LayoutBoard', 'LayoutGallery', 'LayoutBlog'].forEach((layoutName, index) => {
      layout01LayoutsFolder.file(`${layoutName}.js`, `import React from 'react';

const ${layoutName} = ({ children }) => {
  return (
    <div className="${layoutName.toLowerCase()}">
      <header className="layout-header">
        <div className="container">
          <nav className="main-nav">
            <a href="/" className="logo">
              WebBuilder
            </a>
            <ul className="main-menu">
              <li><a href="/">홈</a></li>
              <li><a href="/about">소개</a></li>
              <li><a href="/service">서비스</a></li>
              <li><a href="/contact">문의</a></li>
            </ul>
          </nav>
        </div>
      </header>
      <main className="main-content">
        <div className="container">
          {children}
        </div>
      </main>
      <footer className="layout-footer">
        <div className="container">
          <p>&copy; ${new Date().getFullYear()} ${projectName}. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default ${layoutName};
`);
    });

    // assets 폴더 및 기본 로고 추가
    const layout01AssetsFolder = layout01Folder.folder('assets');
    const layout01ImgFolder = layout01AssetsFolder.folder('img');
    const layout01LogoFolder = layout01ImgFolder.folder('logo');

    // 빈 파일들 추가 (실제 구현에서는 정확한 파일 내용으로 교체)
    layout01LogoFolder.file('logo.png', '');
    layout01LogoFolder.file('logo-2.png', '');

    // 기본 컴포넌트, 헬퍼 등의 폴더 생성
    layout01Folder.folder('components');
    layout01Folder.folder('helpers');
    layout01Folder.folder('hooks');
    layout01Folder.folder('store');
    layout01Folder.folder('wrappers');
    
    // redux 폴더 생성
    const reduxFolder = srcFolder.folder('redux');
    
    // store.js 파일 추가
    reduxFolder.file('store.js', `import { configureStore } from '@reduxjs/toolkit';
import editorReducer from './editorSlice';
import withcookieReducer from './WithcookieSlice';

export const store = configureStore({
  reducer: {
    editor: editorReducer,
    withcookie: withcookieReducer
  }
});
`);
    
    // editorSlice.js 파일 추가
    reduxFolder.file('editorSlice.js', `import { createSlice } from '@reduxjs/toolkit';

const initialState = {
  components: [],
  menus: [],
  layout: {
    selectedLayout: 'layout01',
    layoutProps: {}
  }
};

export const editorSlice = createSlice({
  name: 'editor',
  initialState,
  reducers: {
    // 필요한 액션 리듀서
  }
});

// 선택자 함수들
export const selectComponents = (state) => state.editor.components;
export const selectLayoutInfo = (state) => state.editor.layout;
export const selectMenus = (state) => state.editor.menus;

export default editorSlice.reducer;
`);
    
    // WithcookieSlice.js 파일 추가
    reduxFolder.file('WithcookieSlice.js', `import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

// API 기본 URL 가져오기
export const getApiBaseUrl = () => {
  const hostname = window.location.hostname;
  return hostname === 'localhost' || hostname === '127.0.0.1' ? 'http://localhost:3000' : '';
};

// withcookie 데이터 가져오기 액션
export const fetchWithcookieData = createAsyncThunk(
  'withcookie/fetchWithcookieData',
  async () => {
    try {
      const apiBaseUrl = getApiBaseUrl();
      const response = await fetch(\`\${apiBaseUrl}/api/check_current\`, {
        method: 'GET',
        headers: {'Content-Type': 'application/json'},
        credentials: 'include'
      });
      
      if (!response.ok) throw new Error(\`HTTP error! status: \${response.status}\`);
      return await response.json();
    } catch (error) {
      console.error('Withcookie 데이터 가져오기 실패:', error);
      throw error;
    }
  }
);

// withcookie 슬라이스
export const withcookieSlice = createSlice({
  name: 'withcookie',
  initialState: {
    withcookieData: null,
    loading: false,
    error: null
  },
  reducers: {
    setWithcookieData: (state, action) => {
      state.withcookieData = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchWithcookieData.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchWithcookieData.fulfilled, (state, action) => {
        state.withcookieData = action.payload;
        state.loading = false;
      })
      .addCase(fetchWithcookieData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      });
  }
});

export const { setWithcookieData } = withcookieSlice.actions;
export const selectWithcookieData = (state) => state.withcookie.withcookieData;
export default withcookieSlice.reducer;
`);
    
    // .gitignore 파일 추가
    zip.file('.gitignore', `# See https://help.github.com/articles/ignoring-files/ for more about ignoring files.

# dependencies
/node_modules
/.pnp
.pnp.js

# testing
/coverage

# production
/build

# misc
.DS_Store
.env.local
.env.development.local
.env.test.local
.env.production.local

npm-debug.log*
yarn-debug.log*
yarn-error.log*
`);
    
    // ZIP 파일 생성 및 다운로드
    const content = await zip.generateAsync({ type: 'blob' });
    saveAs(content, `${projectName.replace(/\s+/g, '-').toLowerCase()}-project.zip`);
    
    return { 
      success: true, 
      message: `${projectName} 프로젝트 소스 코드가 성공적으로 생성되었습니다. ZIP 파일이 다운로드됩니다.\n\n압축을 풀고 npm install 후 npm start로 실행할 수 있습니다.` 
    };
  } catch (error) {
    console.error('프로젝트 생성 실패:', error);
    return { success: false, error: error.message };
  }
};

// 이미지 URL 추출 함수
const extractImageUrls = (components) => {
  const imageUrls = [];
  
  components.forEach(component => {
    if (component.type === 'IMAGE' && component.content && component.content.src) {
      imageUrls.push(component.content.src);
    }
  });
  
  return imageUrls;
};

export default buildAndDownloadBuildFolder;